import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { bindActionCreators, compose, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { ComponentDecorator, FormikProps, FormikActions } from 'formik';
import { Editable } from '../../@types';
import { IStore } from '../../modules/root-reducer';

interface IProps {
  get: (id: number) => void;
  update: (entity: Editable) => void;
  clear: () => void;
  deleteFn: (id: number) => void;
  submit: (
    entity: Editable,
    onSuccessfulSubmit: (entity: Editable) => void,
    formikActions: FormikActions<any>
  ) => void;
  goBackOnEditEnd: boolean;
  reducerName: string;
  entityName: string;
  formikEnhancer: ComponentDecorator<any, any>;
}

interface IState {
  formDisabled: boolean;
  isCopy: boolean;
}

export interface IEditContainedProps {
  formDisabled: boolean;
  entity: Editable;
  onEditStart: () => void;
  onEntitySubmit: (entity: Editable, formikActions: FormikActions<any>) => void;
  onEditCancel: () => void;
  deleteFn: (id: number) => void;
  isCopy: boolean;
}

type EditContainerPropTypes = IProps &
  FormikProps<any> &
  RouteComponentProps<any>;

const withEditContainer = (params: IProps) => {
  const hocFunction = (WrappedComponent: any) => {
    const newClass = class extends React.Component<
      EditContainerPropTypes,
      IState
    > {
      state: IState = {
        formDisabled: false,
        isCopy: !!this.props.match.params.parentId
      };

      componentDidMount() {
        const matchParams = this.props.match.params;

        if (matchParams.id) {
          this.props.get(matchParams.id);
        } else if (matchParams.parentId) {
          this.props.get(matchParams.parentId);
        } else {
          this.props.clear();
        }
      }

      // Necessary in the cases that we're going from view to copy and vice versa
      componentDidUpdate(prevProps: EditContainerPropTypes) {
        const matchParams = this.props.match.params;
        const prevParams = prevProps.match.params;

        if (matchParams.id && matchParams.id !== prevParams.id) {
          this.props.get(matchParams.id);
          this.setState({ isCopy: false });
        } else if (
          matchParams.parentId &&
          matchParams.parentId !== prevParams.parentId
        ) {
          this.setState({
            isCopy: true,
            formDisabled: false
          });
        }
      }

      componentWillUnmount() {
        this.props.clear();
      }

      handleEditStart = () => {
        this.setState({
          formDisabled: false
        });
      };

      handleEditCancel = () => {
        this.setState(
          {
            formDisabled: true
          },
          this.props.handleReset
        );
      };

      submitEntity = (entity: Editable, formikActions: FormikActions<any>) => {
        this.props.submit(entity, this.handleSuccessfulSubmit, formikActions);
      };

      handleSuccessfulSubmit = (entity: Editable) => {
        if (this.props.goBackOnEditEnd) {
          this.props.history.goBack();
          return;
        }

        this.setState({
          isCopy: false,
          formDisabled: true
        });

        this.props.update(entity);
      };

      render() {
        const FormikComponent = params.formikEnhancer(WrappedComponent);
        return (
          <FormikComponent
            {...this.props}
            formDisabled={this.state.formDisabled}
            onEditStart={this.handleEditStart}
            onEntitySubmit={this.submitEntity}
            onEditCancel={this.handleEditCancel}
            isCopy={this.state.isCopy}
          />
        );
      }
    };

    const mapStateToProps = (state: IStore) => ({
      entity: state[params.reducerName][params.entityName]
    });

    const mapDispatchToProps = (dispatch: Dispatch) => {
      const { get, update, clear, deleteFn, submit } = params; // ignore closeDialog
      return bindActionCreators(
        { get, update, clear, deleteFn, submit },
        dispatch
      );
    };

    const enhance = compose(
      connect(
        mapStateToProps,
        mapDispatchToProps
      ),
      withRouter
    );

    // need withRouter to push to history
    // not sure how great it is to force a HOC to be wrapped in a HOC
    return enhance(newClass);
  };
  return hocFunction;
};

export default withEditContainer;



// WEBPACK FOOTER //
// ./src/common/edit-container/edit-container.tsx